import { useGetPaymentAuditTrailQuery } from "../api/billingApi";
import { formatCurrency } from "../../../utils/formatters";

interface Props {
  payment: any;
  onClose: () => void;
}

const typeLabel: Record<string, { label: string; color: string }> = {
  created: { label: "Payment Recorded", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  edit_requested: { label: "Edit Requested", color: "bg-orange-100 text-orange-700 border-orange-200" },
  edit_approved: { label: "Edit Approved", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  edit_rejected: { label: "Edit Rejected", color: "bg-red-100 text-red-700 border-red-200" },
  delete_requested: { label: "Delete Requested", color: "bg-rose-100 text-rose-700 border-rose-200" },
  delete_approved: { label: "Deleted", color: "bg-red-100 text-red-700 border-red-200" },
  delete_rejected: { label: "Delete Rejected", color: "bg-slate-100 text-slate-700 border-slate-200" },
  reverified: { label: "Reverified", color: "bg-blue-100 text-blue-700 border-blue-200" },
  // Written directly by the register's Edit Ref button — no approval step, so
  // it is neither a request nor an approval and gets its own label.
  reference_updated: { label: "Reference Corrected", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
};

function DiffRow({ label, from, to }: { label: string; from: any; to: any }) {
  if (from === to || (from == null && to == null)) return null;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-slate-400 font-bold uppercase tracking-widest text-[9px] w-20 flex-shrink-0">{label}</span>
      <span className="line-through text-rose-400 font-mono">{from ?? "—"}</span>
      <svg className="w-3 h-3 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
      <span className="text-emerald-600 font-mono font-bold">{to ?? "—"}</span>
    </div>
  );
}

export default function PaymentAuditTrailModal({ payment, onClose }: Props) {
  const { data, isLoading, isError } = useGetPaymentAuditTrailQuery(payment.id);

  const trail: any[] = data?.trail ?? data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-black text-slate-900 uppercase tracking-widest">
              Audit Trail
            </h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
              Receipt #{payment.receipt_number || payment.reference || payment.id} &nbsp;·&nbsp; {payment.user?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {isLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
              ))}
            </div>
          )}

          {isError && (
            <p className="text-sm text-rose-600 font-bold text-center py-8">
              Could not load audit trail.
            </p>
          )}

          {!isLoading && !isError && trail.length === 0 && (
            <p className="text-sm text-slate-400 font-bold text-center py-8 uppercase tracking-widest">
              No audit records found.
            </p>
          )}

          {!isLoading && trail.length > 0 && (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-100" />

              <div className="space-y-4">
                {trail.map((entry: any, idx: number) => {
                  const meta = typeLabel[entry.type] ?? { label: entry.type, color: "bg-slate-100 text-slate-700 border-slate-200" };
                  return (
                    <div key={idx} className="relative pl-10">
                      {/* Dot */}
                      <div className={`absolute left-2.5 top-1.5 w-3 h-3 rounded-full border-2 border-white ring-2 ${
                        entry.type?.includes("approved") || entry.type === "created"
                          ? "bg-emerald-400 ring-emerald-200"
                          : entry.type?.includes("rejected")
                          ? "bg-rose-400 ring-rose-200"
                          : entry.type?.includes("request")
                          ? "bg-orange-400 ring-orange-200"
                          : "bg-slate-400 ring-slate-200"
                      }`} />

                      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-2">
                        {/* Type badge + date */}
                        <div className="flex items-center justify-between">
                          <span className={`inline-flex px-2 py-0.5 rounded-lg border text-[9px] font-black uppercase tracking-widest ${meta.color}`}>
                            {meta.label}
                          </span>
                          <span className="text-[9px] font-bold text-slate-400">
                            {entry.created_at
                              ? new Date(entry.created_at).toLocaleString("en-NG", {
                                  day: "2-digit", month: "short", year: "numeric",
                                  hour: "2-digit", minute: "2-digit",
                                })
                              : "—"}
                          </span>
                        </div>

                        {/* Actor */}
                        {entry.actor?.name && (
                          <p className="text-[10px] font-bold text-slate-600">
                            By: <span className="text-slate-800">{entry.actor.name}</span>
                          </p>
                        )}

                        {/* Reason / notes */}
                        {entry.reason && (
                          <p className="text-xs text-slate-600 italic">"{entry.reason}"</p>
                        )}
                        {entry.notes && (
                          <p className="text-xs text-slate-400 italic">Note: {entry.notes}</p>
                        )}

                        {/* Proposed changes diff */}
                        {entry.changes && Object.keys(entry.changes).length > 0 && (
                          <div className="space-y-1 pt-1 border-t border-slate-100 mt-1">
                            {/* "Proposed" is wrong for entries that were applied on the spot —
                                an approval, or a direct reference correction. */}
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
                              {entry.type?.includes("request") ? "Proposed Changes" : "Changes"}
                            </p>
                            <DiffRow label="Amount" from={entry.changes.from?.amount != null ? formatCurrency(entry.changes.from.amount) : null} to={entry.changes.to?.amount != null ? formatCurrency(entry.changes.to.amount) : null} />
                            <DiffRow label="Method" from={entry.changes.from?.payment_method} to={entry.changes.to?.payment_method} />
                            <DiffRow label="Date" from={entry.changes.from?.paid_at} to={entry.changes.to?.paid_at} />
                            <DiffRow label="Ref" from={entry.changes.from?.reference} to={entry.changes.to?.reference} />
                          </div>
                        )}

                        {/* Responder */}
                        {entry.responder?.name && (
                          <p className="text-[10px] font-bold text-slate-500">
                            {entry.type?.includes("approved") ? "Approved" : "Rejected"} by:{" "}
                            <span className="text-slate-700">{entry.responder.name}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 pb-5 pt-3 border-t border-slate-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
